import { getPriceListMergedWithNewPrices } from '@app/shared/utils/static-helpers/product-helper';
import { createReducer, on } from '@ngrx/store';
import {
  addSelectedCategory,
  getAllProducts,
  getAllProductsFail,
  getAllProductsSuccess,
  removeSelectedCategory,
  resetProductsState,
  resetProductsPricesState,
  setFilteredProductsSuccess,
  setProductFilters,
  setForcedProductFilters,
  setProductTypeFilters,
  updateSelectedCategories,
  addPricesToUpdate,
  setShowPriceListActions,
  updatePrices,
  resetSelectedProduct,
  setSelectedProduct,
  setSelectedDryProductPackId,
  resetAllSelectedProducts,
  setSelectedWetProductPackId,
  setSelectedTreatsProductPackId,
  removePackFromPriceListToUpdate,
  removePacksFromPriceListToUpdate,
  resetPriceListToUpdate,
  setPriceMargin,
} from './products.actions';
import { initialProductsState } from './products.state';
import { ProductType } from '@app/shared/utils';
import { getInitialFilterValues } from '@app/shared/utils/static-helpers/product-filters-helper';
import { replaceInArray } from '@app/shared/utils/static-helpers/array.utils';

export const productsReducer = createReducer(
  initialProductsState,

  /** Price Actions */
  on(addPricesToUpdate, (state, { prices }) => {
    return {
      ...state,
      priceListToUpdate: getPriceListMergedWithNewPrices(state.priceListToUpdate, prices),
    };
  }),
  on(removePackFromPriceListToUpdate, (state, { productId, packEan }) => {
    return {
      ...state,
      priceListToUpdate: [...state.priceListToUpdate].filter((p) => p.productId !== productId || p.pack.ean !== packEan),
    };
  }),
  on(removePacksFromPriceListToUpdate, (state, { products }) => {
    return {
      ...state,
      priceListToUpdate: state.priceListToUpdate.filter(
        (p) => !products.some(({ productId, packEan }) => p.productId === productId && p.pack.ean === packEan),
      ),
    };
  }),
  on(resetPriceListToUpdate, (state) => ({ ...state, priceListToUpdate: initialProductsState.priceListToUpdate })),
  on(setShowPriceListActions, (state, { value }) => {
    return {
      ...state,
      showPriceListActions: value,
    };
  }),
  on(updatePrices, (state, { updateAllPrices }) => {
    return {
      ...state,
      priceListUpdateLoading: updateAllPrices,
    };
  }),
  on(resetProductsPricesState, (state) => ({
    ...state,
    priceListToUpdate: initialProductsState.priceListToUpdate,
    showPriceListActions: initialProductsState.showPriceListActions,
    priceListUpdateLoading: initialProductsState.priceListUpdateLoading,
  })),
  on(setPriceMargin, (state, { priceMargin }) => {
    return {
      ...state,
      priceMargin,
    };
  }),

  /** All Product Actions */
  on(getAllProducts, (state) => ({
    ...state,
    getAllProductsFail: false,
    productsLoading: true,
  })),
  on(getAllProductsSuccess, (state, { allProductList }) => ({
    ...state,
    allProductList,
    productsLoading: false,
  })),
  on(getAllProductsFail, (state) => ({
    ...state,
    getAllProductsFail: true,
    productsLoading: false,
  })),
  on(setFilteredProductsSuccess, (state, { filteredProductList }) => {
    let filteredProductLength = 0;
    Object.entries(filteredProductList).forEach(([_, productList]) => (filteredProductLength += productList.length ?? 0));
    return {
      ...state,
      filteredProductList,
      filteredProductLength,
    };
  }),

  /** Products Filters Actions */
  on(setProductFilters, (state, { filtersValues }) => {
    return {
      ...state,
      filtersValues,
    };
  }),
  on(updateSelectedCategories, (state, { selectedCategories }) => {
    return {
      ...state,
      selectedCategories,
    };
  }),
  on(addSelectedCategory, (state, { category }) => {
    return { ...state, selectedCategories: state?.selectedCategories?.length ? [...state?.selectedCategories, category] : [category] };
  }),
  on(removeSelectedCategory, (state, { category }) => {
    return { ...state, selectedCategories: state?.selectedCategories?.filter((v) => v !== category) };
  }),
  on(setForcedProductFilters, (state, { specieCode, lifestageType }) => {
    return {
      ...state,
      specificSpecieCode: specieCode || null,
      specificLifestageType: lifestageType || null,
      filtersValues: getInitialFilterValues(state.filtersValues, { specieCode, lifestageType }),
    };
  }),
  on(setProductTypeFilters, (state, { dry, wet, treats }) => {
    return {
      ...state,
      filtersValues: {
        ...state.filtersValues,
        wet: wet,
        dry: dry,
        treats: treats,
      },
    };
  }),

  /** Products Catalog Actions */
  on(setSelectedDryProductPackId, (state, { packId }) => {
    const currentSelectedDryProduct = state?.selectedProducts?.find((item) => item?.product?.productType === ProductType.Dry)?.product;

    return currentSelectedDryProduct
      ? {
          ...state,
          selectedProducts: replaceInArray(
            state?.selectedProducts,
            {
              product: currentSelectedDryProduct,
              packId: packId,
            },
            (item) => item?.product?.productType,
            true,
          ),
        }
      : state;
  }),
  on(setSelectedWetProductPackId, (state, { packId }) => {
    const currentSelectedWetProduct = state?.selectedProducts?.find((item) => item?.product?.productType === ProductType.Wet)?.product;

    return currentSelectedWetProduct
      ? {
          ...state,
          selectedProducts: replaceInArray(
            state?.selectedProducts,
            {
              product: currentSelectedWetProduct,
              packId: packId,
            },
            (item) => item?.product?.productType,
            true,
          ),
        }
      : state;
  }),
  on(setSelectedTreatsProductPackId, (state, { packId }) => {
    const currentSelectedTreatsProduct = state?.selectedProducts?.find(
      (item) => item?.product?.productType === ProductType.Treats,
    )?.product;

    return currentSelectedTreatsProduct
      ? {
          ...state,
          selectedProducts: replaceInArray(
            state?.selectedProducts,
            {
              product: currentSelectedTreatsProduct,
              packId: packId,
            },
            (item) => item?.product?.productType,
            true,
          ),
        }
      : state;
  }),
  on(setSelectedProduct, (state, action) => {
    const newPackId = action.selectedProduct?.packId || action.selectedProduct?.product?.packages[0]?.sCode || null;
    const existingPackId = state.selectedProducts.find((item) => item.product?.id === action.selectedProduct?.product?.id)?.packId || null;
    const packIdToSet = action.setProductOnly ? existingPackId : newPackId;
    const newProduct = { product: action?.selectedProduct?.product, packId: packIdToSet };

    return action.allowMultipleSelection
      ? {
          ...state,
          selectedProducts: [...(state?.selectedProducts || []), newProduct],
        }
      : {
          ...state,
          selectedProducts: replaceInArray(state?.selectedProducts, newProduct, (item) => item?.product?.productType, true),
        };
  }),
  on(resetSelectedProduct, (state, action) => {
    return {
      ...state,
      selectedProducts: state?.selectedProducts?.filter((item) => item?.product?.id !== action.productId),
    };
  }),
  on(resetAllSelectedProducts, (state) => ({ ...state, selectedProducts: initialProductsState.selectedProducts })),
  on(resetProductsState, () => initialProductsState),
);
